import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import { Tabs, Tab } from 'material-ui/Tabs'
import SwipeableViews from 'react-swipeable-views'
import {
  toggleActiveReviewMessage,
  updateMessageSetting,
  updateReviewTypesOrOffset,
} from '../../../state/actions/message-settings'
import Help from '../Help'
import HelpButton from '../HelpButton'
import EmailHelperText from './EmailHelperText'
import TextHelperText from './TextHelperText'
import SettingsOptions from './SettingsOptions'
import { logException } from '../../../services/sentry'
import { renderAlert } from '../../../services/render-alert'
import styles from './TabStyles'
import './styles.css'
import TextEditors from './PostApptReviewSettingsEditors/TextEditors'
import EmailEditors from './PostApptReviewSettingsEditors/EmailEditors'

class PostApptReviewMessageEditor extends Component {
  constructor(props) {
    super(props)
    this.state = {
      isHelpOpen: false,
      slideIndex: 0,
    }
  }

  updateActiveSetting = (messageType, toggle) => {
    const { locationId, toggleReviewTypeActive } = this.props
    toggleReviewTypeActive(locationId, messageType, toggle)
  }

  handleSlide = (value) => {
    this.setState({ slideIndex: value })
  }

  updateSetting = ({ body, subject, always_send }, settingId) => {
    const { updateOneSetting } = this.props
    const changes = {
      body,
      subject,
      always_send,
    }

    return updateOneSetting(settingId, changes).catch((err) => {
      logException(err)
    })
  }

  toggleHelp = () => {
    const { isHelpOpen } = this.state
    this.setState({
      isHelpOpen: !isHelpOpen,
    })
  }

  updateReviewsTypesOrOffset = (offset, apptTypeArr) => {
    const { locationId, setTypesOrOffset } = this.props
    const setting = {
      offset,
      appointment_type_ids: apptTypeArr,
      location_id: locationId,
    }
    return setTypesOrOffset(setting, locationId).then((res) => {
      const msgItem = offset ? 'Schedule' : 'Appointment Types'
      if (!res) {
        renderAlert(`Error updating ${msgItem}.`, 'error')
        return Promise.resolve(null)
      }
      renderAlert(`${msgItem} updated!`, 'success')
      return Promise.resolve(res)
    })
  }
  render() {
    const { isHelpOpen, slideIndex } = this.state
    const {
      hasReviews,
      hasTopsRemindersPlus,
      isLoaded,
      maxChars,
      pmsType,
      pmsVersion,
      postApptReviewMessages,
      postApptReviewMessages: {
        followUpEmail,
        followUpText,
        initialRequestEmail,
        initialRequestText,
      },
      reviewsOffset,
      tier,
    } = this.props
    const showMessages =
      postApptReviewMessages && (hasReviews || hasTopsRemindersPlus)
    if (!showMessages) {
      return (
        <div className="container">
          <h1>Post-Appointment Reviews</h1>
          <div>Contact Support for access to this feature.</div>
        </div>
      )
    }

    const emailsActive = !!initialRequestEmail && initialRequestEmail.active
    const textsActive = !!initialRequestText && initialRequestText.active
    const showTexts = tier !== 4

    const emailSettings = [
      {
        pmsType,
        pmsVersion,
        setting: initialRequestEmail,
        title: 'Initial Request Email',
        updateSetting: this.updateSetting,
      },
      {
        pmsType,
        pmsVersion,
        setting: followUpEmail,
        title: 'Follow-Up Email',
        updateSetting: this.updateSetting,
      },
    ]

    const textSettings = [
      {
        maxChars,
        pmsType,
        pmsVersion,
        setting: initialRequestText,
        title: 'Initial Request Text',
        updateSetting: this.updateSetting,
      },
      {
        maxChars,
        pmsType,
        pmsVersion,
        setting: followUpText,
        title: 'Follow-Up Text',
        updateSetting: this.updateSetting,
      },
    ]

    return (
      <div className="container">
        <h1>Post-Appointment Reviews</h1>
        <div>
          <div className="helpButtonContainer">
            <HelpButton onTouchTap={this.toggleHelp} />
          </div>
          {isHelpOpen && (
            <Help
              hasPlum={false}
              hasReviews
              onlyVars
              pmsType={pmsType}
              pmsVersion={pmsVersion}
            />
          )}
          <SettingsOptions
            emailsActive={emailsActive}
            handleActiveToggle={this.updateActiveSetting}
            isLoaded={isLoaded}
            reviewsOffset={reviewsOffset}
            textsActive={textsActive}
            updateSetting={this.updateReviewsTypesOrOffset}
            usedAppointmentTypeIds={initialRequestText.appointment_types}
            tier={tier}
          />
          {!showTexts ? (
            <div>
              <EmailEditors editors={emailSettings} />
              <div style={styles.helpColumn}>
                <EmailHelperText />
              </div>
            </div>
          ) : (
            <div>
              <Tabs
                className="ThreadTabs"
                onChange={this.handleSlide}
                value={slideIndex}
                style={styles.tabs}
                inkBarStyle={styles.underline}
                tabItemContainerStyle={styles.tabsContainer}
              >
                <Tab
                  className="ThreadTab"
                  style={styles.tab}
                  label="Emails"
                  value={0}
                />
                <Tab
                  className="ThreadTab"
                  style={styles.tab}
                  label="Texts"
                  value={1}
                />
              </Tabs>
              <SwipeableViews
                index={slideIndex}
                value={this.handleSlide}
                style={styles.swipableThreads}
              >
                <div style={styles.emails}>
                  <EmailEditors editors={emailSettings} />
                  <div style={styles.helpColumn}>
                    <EmailHelperText />
                  </div>
                </div>
                <div style={styles.texts}>
                  <TextEditors editors={textSettings} />
                  <div style={styles.helpColumn}>
                    <TextHelperText />
                  </div>
                </div>
              </SwipeableViews>
            </div>
          )}
        </div>
      </div>
    )
  }
}

PostApptReviewMessageEditor.propTypes = {
  hasReviews: PropTypes.bool.isRequired,
  hasTopsRemindersPlus: PropTypes.bool.isRequired,
  isLoaded: PropTypes.bool.isRequired,
  locationId: PropTypes.string,
  maxChars: PropTypes.number,
  pmsType: PropTypes.string,
  pmsVersion: PropTypes.number,
  postApptReviewMessages: PropTypes.objectOf(
    PropTypes.shape({
      active: PropTypes.bool.isRequired,
      always_send: PropTypes.bool.isRequired,
      created_at: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      message_setting_type: PropTypes.string.isRequired,
      message_type: PropTypes.string.isRequired,
      offset: PropTypes.number.isRequired,
      practice_id: PropTypes.number.isRequired,
      updated_at: PropTypes.string.isRequired,
    }),
  ),
  reviewsOffset: PropTypes.number,
  setTypesOrOffset: PropTypes.func.isRequired,
  tier: PropTypes.number.isRequired,
  toggleReviewTypeActive: PropTypes.func.isRequired,
  updateOneSetting: PropTypes.func.isRequired,
}

const mapStateToProps = ({
  account: {
    auth: { hasTopsRemindersPlus, pmsType, pmsVersion, tier },
  },
  messageSettings: { isLoaded, reviewsOffset },
}) => ({
  hasTopsRemindersPlus,
  pmsType,
  pmsVersion,
  isLoaded,
  reviewsOffset,
  tier,
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setTypesOrOffset: (setting, locationId) =>
        updateReviewTypesOrOffset(setting, locationId),
      toggleReviewTypeActive: (locationId, messageType, toggle) =>
        toggleActiveReviewMessage(locationId, messageType, toggle),
      updateOneSetting: (settingId, changes) =>
        updateMessageSetting(settingId, changes),
    },
    dispatch,
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PostApptReviewMessageEditor)



// WEBPACK FOOTER //
// ./src/components/Settings/PostApptReviewMessageEditor/index.js